import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/api';
import '../styles/AuthPages.css';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string>('');

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading) return;

    // Basic validation
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authService.forgotPassword(formData.email);
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Forgot password error:', err);
      
      const apiError = err.response?.data;
      
      // Handle different error types
      if (apiError?.code) {
        switch (apiError.code) {
          case 'RATE_LIMIT_EXCEEDED':
            setError('Too many attempts. Please try again later.');
            break;
          case 'MISSING_EMAIL':
            setError('Please enter your email address.');
            break;
          default:
            setError(apiError.error || 'An error occurred. Please try again.');
        }
      } else {
        setError(err.response?.data?.error || 'Unable to connect to server. Please check your internet connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Check Your Email 📧</h2>
          </div>
          
          <div className="success-message">
            <div className="success-icon">✅</div>
            <p>
              If an account with <strong>{formData.email}</strong> exists, we've sent you a password reset link.
            </p>
          </div>

          <div className="info-box">
            <h4>What to do next:</h4>
            <ol>
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the password reset link in the email</li>
              <li>Follow the instructions to set a new password</li>
              <li>The link will expire in 1 hour for security</li>
            </ol>
          </div>

          <div className="auth-footer">
            <p>
              Remember your password?{' '}
              <Link to="/login" className="auth-link">
                Sign In
              </Link>
            </p>
            <p>
              Didn't receive an email?{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({ email: '' });
                }}
              >
                Try Again
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Reset Your Password 🔐</h2>
          <p>Enter your email address and we'll send you a link to reset your password.</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-content">
                <span className="spinner"></span>
                Sending Reset Link...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className="security-info">
          <h4>🔒 Security Information</h4>
          <ul>
            <li>The password reset link will expire in 1 hour</li>
            <li>The link can only be used once</li>
            <li>For your security, we won't confirm if this email exists</li>
          </ul>
        </div>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="auth-link">
              Sign In
            </Link>
          </p>
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">
              Create Account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;